import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import AudioTranscriber from "./components/AudioTranscriber";
import { ToastContainer } from "react-toastify";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Settings from "./components/settings/Settings";
import FeedbackPage from "./components/feedback/FeedbackPage";
import TermsAndConditionsDarkPage from "./components/terms-and-conditions/TermsAndConditionsDark";
import TutorialPage from "./components/tutorial/TutorialPage";
import { ClerkProvider, useAuth, SignIn } from "@clerk/clerk-react";
import ClerkAuthProvider from "./components/auth/ClerkAuthProvider";
import { CircularProgress, Typography, Container, Button } from "@mui/material";
import Login from "./components/login/Login";
import PrivacyPolicy from "./components/privacy-policy/PrivacyPolicy";
import TermsOfService from "./components/terms-of-service/TermsOfService";
import AdminRegistration from "./components/admin/AdminRegistration";
import VerifyAdmin from "./components/admin/VerifyAdmin";
import AdminDashboard from "./components/admin/AdminDashboard";
import InvitationSignup from "./components/auth/InvitationSignup";
import VerifyInvitationSignup from "./components/auth/VerifyInvitationSignup";
import Header from "./components/common/Header";
import HistoryPage from "./components/history/HistoryPage";
import { RootState } from "./store/store";
import InvitationManagement from "./components/admin/InvitationManagement";
import { AppSettingsProvider, useAppSettings } from "./contexts/AppSettingsContext";
import AppThemeProvider from "./AppThemeProvider";
import TitleUpdater from "./components/common/TitleUpdater";
import HomePage from "./components/home/HomePage";
import Contact from "./components/contact/Contact";
import PricingPage from "./components/pricing/PricingPage";

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || '';
const WEBSITE_ENABLED = process.env.REACT_APP_WEBSITE_ENABLED === 'true';

if (!clerkPubKey) {
  throw new Error("Missing Clerk Publishable Key");
}

// Loading screen component that shows while settings are loading
const LoadingScreen = () => {
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f4f6f8'
      }}
    >
      <CircularProgress size={60} />
    </Box>
  );
};

// Wrapper component that handles the loading state
const AppContent = () => {
  const { loading: settingsLoading } = useAppSettings();
  const { isSignedIn, isLoaded } = useAuth();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previousSignedIn, setPreviousSignedIn] = useState<boolean | null>(null);

  // Track authentication state changes
  useEffect(() => {
    if (isLoaded && previousSignedIn !== null && previousSignedIn !== isSignedIn) {
      // Authentication state is changing
      setIsTransitioning(true);
      
      // Clear transition state after a brief delay
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 100);
      
      return () => clearTimeout(timer);
    }
    
    if (isLoaded) {
      setPreviousSignedIn(isSignedIn);
    }
  }, [isSignedIn, isLoaded, previousSignedIn]);

  // If settings are still loading on initial render AND we don't have cached settings, show loading screen
  if (settingsLoading && !localStorage.getItem('app-settings')) {
    return <LoadingScreen />;
  }

  // During transitions, maintain the current UI to prevent flashing
  return (
    <Box 
      width={"100%"} 
      sx={{ 
        opacity: isTransitioning ? 0.95 : 1,
        transition: 'opacity 0.1s ease-in-out'
      }}
    >
      <ToastContainer />
      <Routes>
        {/* Conditional root route based on website mode */}
        <Route 
          path="/" 
          element={
            WEBSITE_ENABLED ? <HomePage /> : <Navigate to="/login" replace />
          } 
        />
        
        {/* Public website routes - only available when website mode is enabled */}
        {WEBSITE_ENABLED && (
          <>
            <Route path="/contact" element={<Contact />} />
            <Route path="/pricing" element={<PricingPage />} />
          </>
        )}
        
        {/* Authentication pages */}
        <Route path="/login/*" element={<Login />} />
        <Route path="/login/sso-callback" element={<SSOCallback />} />
        <Route path="/sso-callback" element={<SSOCallback />} />
        <Route path="/signup/verify" element={<VerifyAdmin />} />
        <Route path="/signup/invitation" element={<InvitationSignup />} />
        <Route path="/signup/verify-invitation" element={<VerifyInvitationSignup />} />
        
        {/* Admin registration */}
        <Route path="/admin/register" element={<AdminRegistration />} />
        
        {/* Legal pages - keep these accessible without login */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        
        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AudioTranscriber />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <PageWithHeader>
                <Settings />
              </PageWithHeader>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <PageWithHeader>
                <AdminDashboard />
              </PageWithHeader>
            </ProtectedRoute>
          }
        />
        <Route
          path="/feedback"
          element={
            <ProtectedRoute>
              <PageWithHeader>
                <FeedbackPage />
              </PageWithHeader>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tos"
          element={
            <ProtectedRoute>
              <PageWithHeader>
                <TermsAndConditionsDarkPage />
              </PageWithHeader>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tutorial"
          element={
            <ProtectedRoute>
              <PageWithHeader>
                <TutorialPage />
              </PageWithHeader>
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <PageWithHeader>
                <HistoryPage />
              </PageWithHeader>
            </ProtectedRoute>
          }
        />
        <Route path="/admin-registration" element={<AdminRegistration />} />
        <Route path="/admin/invitations" element={<InvitationManagement />} />
        
        {/* 404 Page - Catch all unmatched routes */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Box>
  );
};

// SSO Callback component
const SSOCallback = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Give Clerk time to process authentication and then redirect
    const timer = setTimeout(() => {
      navigate('/dashboard');
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [navigate]);
  
  return (
    <Box 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      flexDirection="column"
      height="100vh"
    >
      <CircularProgress />
      <Box mt={2}>Completing authentication...</Box>
    </Box>
  );
};

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const [hasBeenAuthenticated, setHasBeenAuthenticated] = useState(false);
  
  useEffect(() => {
    if (isSignedIn) {
      setHasBeenAuthenticated(true);
    }
  }, [isSignedIn]);
  
  // If Clerk is still loading, show loading spinner
  if (!isLoaded) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>;
  }
  
  // If user is not signed in and has never been authenticated, redirect to login
  if (!isSignedIn && !hasBeenAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // If user was authenticated but now isn't (logout scenario), redirect to login
  if (!isSignedIn && hasBeenAuthenticated) {
    setHasBeenAuthenticated(false);
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Layout component with header
const PageWithHeader = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <Header />
      {children}
    </>
  );
};

// NotFound component for 404 page
const NotFound = () => {
  const navigate = useNavigate();
  
  return (
    <Box 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      flexDirection="column" 
      minHeight="100vh"
      padding={3}
      textAlign="center"
    >
      <Typography variant="h1" component="h1" gutterBottom>
        404
      </Typography>
      <Typography variant="h4" component="h2" gutterBottom>
        Page Not Found
      </Typography>
      <Typography variant="body1" paragraph>
        The page you are looking for doesn't exist or has been moved.
      </Typography>
      <Button 
        variant="contained" 
        color="primary" 
        onClick={() => navigate('/')}
        sx={{ mt: 2 }}
      >
        Go to Home
      </Button>
    </Box>
  );
};

function App() {
  // Memoize the ClerkProvider options to prevent unnecessary re-renders
  const clerkOptions = React.useMemo(() => ({
    // Prevent Clerk from automatically redirecting
    afterSignInUrl: undefined,
    afterSignUpUrl: undefined,
    afterSignOutUrl: "/login",
    // Keep session in sync without full re-renders
    syncSessionWithTab: true,
  }), []);

  return (
    <ClerkProvider 
      publishableKey={clerkPubKey}
      {...clerkOptions}
    >
      <ClerkAuthProvider>
        <AppSettingsProvider>
          <AppThemeProvider>
            <TitleUpdater />
            <AppContent />
          </AppThemeProvider>
        </AppSettingsProvider>
      </ClerkAuthProvider>
    </ClerkProvider>
  );
}

export default App;
